import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { History, Calendar, ChevronRight, Activity, Search } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PatientHistory = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [diagnoses, setDiagnoses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/diagnoses/history`);
      setDiagnoses(response.data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatMonth = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(language === 'pt' ? 'pt-PT' : 'en-US', { month: 'long' }) + ' ' + date.getFullYear();
  };

  const filteredDiagnoses = diagnoses.filter(d =>
    d.symptom_name.toLowerCase().includes(search.toLowerCase())
  );

  // Group by month
  const groupedDiagnoses = filteredDiagnoses.reduce((acc, diagnosis) => {
    const key = formatMonth(diagnosis.created_at);
    if (!acc[key]) acc[key] = [];
    acc[key].push(diagnosis);
    return acc;
  }, {});

  return (
    <div className="max-w-2xl mx-auto px-6 py-8" data-testid="patient-history">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-center gap-3 mb-2">
          <History className="w-6 h-6 text-primary" />
          <h1 className="text-3xl font-bold">{t('assessmentHistory')}</h1>
        </div>
        <p className="text-muted-foreground mb-6">
          {t('viewAllPastAssessments')}
        </p>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder={t('searchBySymptom')}
            className="pl-12 rounded-full"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="history-search"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : Object.keys(groupedDiagnoses).length > 0 ? (
          <div className="space-y-8">
            {Object.entries(groupedDiagnoses).map(([month, monthDiagnoses]) => (
              <div key={month}>
                <span className="label-medical mb-3 block">{month}</span>
                <div className="space-y-3">
                  {monthDiagnoses.map((diagnosis, index) => (
                    <motion.div
                      key={diagnosis.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <Card
                        className="p-5 rounded-2xl cursor-pointer hover:bg-accent transition-all card-hover"
                        onClick={() => navigate(`/results/${diagnosis.id}`)}
                        data-testid={`history-item-${index}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                              <Activity className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                              <h3 className="font-semibold">{diagnosis.symptom_name}</h3>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                                <Calendar className="w-3.5 h-3.5" />
                                {formatDate(diagnosis.created_at)}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            {diagnosis.possible_conditions.length > 0 ? (
                              <div className="text-sm">
                                <span className="text-muted-foreground">{t('topMatch')}: </span>
                                <span className="font-medium">{diagnosis.possible_conditions[0].name}</span>
                              </div>
                            ) : (
                              <span className="text-sm text-muted-foreground">{t('noConditionsFound')}</span>
                            )}
                            <ChevronRight className="w-5 h-5 text-muted-foreground mt-1 ml-auto" />
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Card className="p-8 rounded-2xl text-center">
            <History className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold mb-2">{t('noHistoryYet')}</h3>
            <p className="text-muted-foreground text-sm">
              {t('completedAssessmentsWillAppear')}
            </p>
          </Card>
        )}
      </motion.div>
    </div>
  );
};
